import { AdTemplate } from '@/components/AdTemplate';
import { AuthorDetails } from '@/components/AuthorDetails';
import { Layout } from '@/components/Layout';
import { Pagination } from '@/components/Pagination';
import { Search } from '@/components/Search';
import { SoMeProps } from '@/components/SoMe';
import { BylineData } from '@/types/bylineData';
import { getAbTestVariants } from '@/utils/getAbTestVariants';
import { getCurrentBrand } from '@/utils/getCurrentBrand';
import { getUrl } from '@/utils/getUrl';
import { http } from '@/utils/http';
import { jsonify } from '@/utils/jsonify';
import { CLOUDFLARE_TTL } from '@/utils/ttl';
import { withCacheControlHeaders } from '@/utils/withCacheControlHeaders';
import { withSafeInnerHTML } from '@/utils/withSafeInnerHTML';
import { withSerializationGuard } from '@/utils/withSerializationGuard';
import { PlaylistInterface } from 'base/components/JwVideo/Article/utils/getVideoInformation';
import { publisher } from 'base/components/SchemaMarkup/utils';
import { getLabradorConfig } from 'lib/configs/labrador';
import { loadAdsData } from 'lib/data';
import { ConsolidatedAuthor, fetchAuthorArticles, getAuthorBySlug } from 'lib/labrador/author-page';
import { AdsMeta } from 'lib/labrador/types';
import { GetServerSideProps, NextPage } from 'next';
import Head from 'next/head';
import { ProfilePage, WithContext } from 'schema-dts';
import { isTruthy } from 'typesafe-utils';

interface PageAuthorsProps {
  author: ConsolidatedAuthor;
  searchResults: BylineData;
  adsData: AdsMeta;
  titleSuffix: string;
  playlist?: PlaylistInterface;
}

const PageAuthors: NextPage<PageAuthorsProps> = ({ author, searchResults, adsData, titleSuffix, playlist }) => {
  const src = author?.imageBig?.replace('%3F', '?');
  const authorName = `${author.firstName} ${author.lastName}`;
  const brandName = getCurrentBrand().name;
  const publishedUrl = `/vara-skribenter/${author.slug}`;
  const canonical = getUrl(publishedUrl)?.href;
  const { totalItems = 0, limit = 0, start = 0 } = searchResults || {};
  const pageNumberInTitle = start > 0 ? ` - Sida ${start + 1}` : '';

  const soMeList = [
    { name: 'facebook', href: author.facebook },
    { name: 'x', href: author.twitter },
    { name: 'instagram', href: author.instagram },
  ].filter(({ href }) => isTruthy(href)) as SoMeProps[];

  const description = `Artiklar skrivna av ${authorName}. Läs alla artiklar av ${authorName} på ${brandName}.`;

  const markupSchema: WithContext<ProfilePage> = {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    mainEntity: {
      '@type': 'Person',
      name: authorName,
      description: author.description || '',
      url: canonical || '',
      image: src || undefined,
      mainEntityOfPage: canonical || '',
    },
    hasPart: {
      '@type': 'CollectionPage',
      isPartOf: canonical || '',
      mainEntity: {
        '@type': 'ItemList',
        numberOfItems: totalItems,
        itemListElement: searchResults?.items?.map((article, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: article.link,
        })),
      },
    },
    publisher,
  };

  return (
    <Layout
      content={
        <>
          <Head>
            <title>{`${authorName}${pageNumberInTitle} ${titleSuffix}`}</title>
            <meta name="title" content={`${authorName}${pageNumberInTitle} ${titleSuffix}`} key="metaTitle" />
            <meta name="description" content={description} key="metaDescription" />
            <link rel="canonical" href={canonical} />
          </Head>
          <AdTemplate adsData={adsData} />
          <script type="application/ld+json" {...withSafeInnerHTML(jsonify(markupSchema))} suppressHydrationWarning />
          {start === 0 && !author.isRetired && (
            <AuthorDetails
              title={authorName}
              image={{ src, alt: authorName }}
              description={author.description}
              soMeList={soMeList}
              playlist={playlist}
              caption={<>{author.publicEmail && <a href={`mailto:${author.publicEmail}`}>{author.publicEmail}</a>}</>}
            />
          )}

          <Search
            hideButton={true}
            query={`${authorName}`}
            total={totalItems}
            adsUniqueId={adsData?.adsUniqueId}
            initial={{ next: searchResults?.nextPage, results: searchResults?.items }}
            page={start + 1}
            pageType="authorPage"
          />
          {totalItems > limit && (
            <Pagination pages={Math.ceil(totalItems / Math.max(limit, 1))} page={start + 1} maxSteps={5} />
          )}
        </>
      }
    />
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { slug, params = [] } = context.params as { slug: string; params?: string[] };
  const pageNumberParam = params[0];

  if (Number(pageNumberParam) === 1) {
    return { redirect: { destination: `/vara-skribenter/${encodeURIComponent(slug)}`, permanent: true } };
  }

  const isPageNumberInvalid = pageNumberParam && (!Number(pageNumberParam) || Number(pageNumberParam) < 1);
  const hasMoreParams = params.length > 1;

  if (isPageNumberInvalid || hasMoreParams) {
    return { notFound: true };
  }

  const author = await getAuthorBySlug(slug);
  if (!author) {
    return { notFound: true };
  }

  const createName = (author?: { firstName?: string; lastName?: string }) => `${author?.firstName}%${author?.lastName}`;

  const alternativeAuthorNames = author.alternativeNames.map(createName).join(',');
  const startFrom = parseInt(pageNumberParam) > 0 ? parseInt(pageNumberParam) : 1;
  const searchResults = await fetchAuthorArticles(createName(author), alternativeAuthorNames, startFrom - 1);
  const maxPages = Math.ceil((searchResults?.totalItems ?? 1) / (searchResults?.limit ?? 1));
  if (pageNumberParam && Number(pageNumberParam) > maxPages) {
    // redirect to author root
    return { redirect: { destination: `/vara-skribenter/${encodeURIComponent(slug)}`, permanent: false } };
  }
  const { clientAdsData: adsData } = await loadAdsData();

  const titleSuffix = await getLabradorConfig('miscsettings.site.siteTitleSuffix');
  const route = ['vara-skribenter', slug, pageNumberParam];

  const playlist = author.videoId
    ? (
        await http.get<{ playlist: PlaylistInterface }>(
          `https://cdn.jwplayer.com/v2/sites/3VFCRfhD/media/${author.videoId}/playback.json`,
        )
      )?.data?.playlist
    : undefined;

  withCacheControlHeaders(context.res, CLOUDFLARE_TTL);

  return {
    props: withSerializationGuard({
      key: route.join('/'),
      author,
      searchResults,
      adsData,
      titleSuffix,
      playlist,
      abTestVariants: getAbTestVariants(context),
    }),
  };
};

export default PageAuthors;
